import React, { useEffect, useImperativeHandle, useMemo, useRef, UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellProps,
  Column,
  ColumnInstance,
  Filters,
  FilterValue,
  HeaderGroup,
  Hooks,
  IdType,
  Row,
  SortingRule,
  TableInstance,
  useExpanded,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { useSelector } from 'react-redux';

import MTableView from './MTable.view';
import MTableCellExpandRow from './MTableCellExpandRow/MTableCellExpandRow';
import MTableCellExpandCheck from './MTableCells/MTableCellExpandCheck/MTableCellExpandCheck';
import MTableCellExpendCol from './MTableCells/MTableCellExpendCol/MTableCellExpendCol';

import { FilterItem, FilterMetadata, MColumnOptions, RowData, TableRef } from './Table.model';
import { AppState } from '../../models/app-store';

type Props<T extends RowData> = {
  isDraggable?: boolean;
  columns: (keyof T)[];
  className?: string;
  columnsOptions?: MColumnOptions<T>;
  scrollTableRef?: React.RefObject<HTMLDivElement>;
  crossSelectedRef?: React.RefObject<T[]>;
  data: T[];
  disableFilters?: boolean;
  filters?: Filters<T>; // pass values for filters
  initialFilters?: Filters<T>;
  hideSelection?: boolean;
  disableSelection?: boolean;
  disableSortBy?: boolean;
  loading?: boolean;
  manualFilters?: boolean;
  manualSortBy?: boolean;
  minHeight?: number;
  noResultsClassName?: string;
  page?: number;
  pageCount?: number;
  openExpandLabel?: string;
  closeExpandLabel?: string;
  selectAll?: boolean;
  selectedRow?: Partial<T> | Partial<T>[] | null; // for single selecting row
  sort?: SortingRule<T>[];
  tableInstance?: TableInstance<T>;
  rowClassName?: string;
  hiddenColumns?: string[];
  hiddenHeader?: boolean;
  columnCenter?: boolean;
  selectTable?: boolean;
  hideNoResultDarkIcon?: boolean;
  noResultImage?: string;
  noResultsText?: string;
  noResultsSubHeaderText?: string;
  getRowId?: (row: T) => number | string;
  onFiltersChange?: (filters: FilterItem<T>) => void;
  onRowClick?: (row: Row<T>) => void; // in most cases for single selecting row. but can be used for other stuff
  onRowHover?: (row: number | null) => void;
  onSelect?: (selected: number[]) => void;
  onSortChange?: (sortValue: SortingRule<T>[]) => void;
  renderEdit?: (row: Row<T>) => React.ReactElement | null;
  renderExpand?: (row: Row<T>) => React.ReactElement | null;
  onFiltersRender?: (filters: { id: IdType<T>; jsxFunc: () => React.ReactNode }[]) => void;
  onSelectionClick?: () => void;
  onEndOfScroll?: (scrollPosition: number) => void;
  onMoveRow?: (fromIndex: number, toIndex: number) => void;
};

const MTable = <T extends RowData>(props: React.PropsWithChildren<Props<T>>, ref: React.ForwardedRef<TableRef<T>>): JSX.Element => {
  const { t } = useTranslation();
  const darkMode = useSelector((state: AppState) => state.ui.darkMode);

  // Generate columns of the table
  const columns: Column<T>[] = useMemo(() => {
    const columnsOptionsCols = Object.keys(props.columnsOptions || {}).filter(
      (columnOptionName: string) => !props.columns.includes(columnOptionName),
    );

    const configs: Column<T>[] = [...props.columns, ...columnsOptionsCols].map((columnName: keyof T) => {
      const textColumnName = String(columnName);

      const config: Column<T> = {
        width: 'auto',
        accessor: columnName,
        Header: textColumnName[0].toUpperCase() + textColumnName.slice(1),
        ...props.columnsOptions?.[columnName],
      };

      if (props.columnsOptions?.[columnName]?.expander) {
        config.Cell = MTableCellExpandRow;
      }

      return config;
    });

    if (!props.hideSelection) {
      configs.unshift({
        accessor: 'selection',
        // Return the below to have button for all selection in the table
        // Header: (item: CellProps<T>) =>
        //   MTableExpandCheckHeader({
        //     TableToggleAllRowsSelectedProps: item.getToggleAllPageRowsSelectedProps,
        //     selectAll: props.selectAll,
        //     disableSelection: props.disableSelection,
        //   }),
        Header: () => null,
        Cell: (item: CellProps<T>) =>
          MTableCellExpandCheck({
            ...item,
            disableSelection: props.disableSelection,
            rowCheckbox: true,
            onToggle: props.onSelectionClick,
          }),
        width: '1%',
        disableSortBy: true,
        disableFilters: true,
      });
    }

    if (props.renderExpand && props.openExpandLabel && props.closeExpandLabel) {
      configs.push({
        accessor: 'expander',
        Header: () => t('settings.parameters.actions'),
        width: 1,
        disableSortBy: true,
        disableFilters: true,
        Cell: (item: CellProps<T>) =>
          MTableCellExpendCol({ ...item, openExpandLabel: props.openExpandLabel, closeExpandLabel: props.closeExpandLabel }),
      });
    }

    return configs;
  }, [t, props.columns, props.columnsOptions, props.hideSelection, props.disableSelection, props.openExpandLabel, props.closeExpandLabel]);

  const getRowId = props.getRowId;

  const crossPageSelection = (newState, action) => {
    if (action.type === 'resetSelectedRows' && props.crossSelectedRef?.current) {
      const currentPageSelectedRowIds = {};

      for (const selectedRow of props.crossSelectedRef.current) {
        if (props.data.find((item: T) => item.id == selectedRow.id)) {
          currentPageSelectedRowIds[selectedRow.id!] = true;
        }
      }

      return { ...newState, selectedRowIds: currentPageSelectedRowIds };
    }

    return newState;
  };

  const baseTable = useTable<T>(
    {
      columns,
      data: props.data,
      disableFilters: props.disableFilters,
      manualFilters: props.manualFilters,
      manualSortBy: props.manualSortBy,
      manualPagination: true,
      initialState: { sortBy: props.sort || [], pageIndex: props.page },
      pageCount: props.pageCount,
      disableSortBy: props.disableSortBy,
      getRowId: getRowId ? (row: T) => getRowId(row).toString() : undefined,
      stateReducer: crossPageSelection,
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks: Hooks<T>) => {
      if (props.onSelect) {
        hooks.visibleColumns.push((columns: ColumnInstance<T>[]) => columns);
      }
    },
  );

  const table: TableInstance<T> = props.tableInstance || baseTable;
  const renderedFiltersRef = useRef<{ ids: string; filters: Filters<T> } | null>(null);

  const promptFiltersChange = (field: IdType<T>, value: FilterValue, metadata?: FilterMetadata) => {
    const newFilters: FilterItem<T> = [...table.state.filters];
    const filterItemIndex = newFilters.findIndex((filter: { id: IdType<T>; value: FilterValue }) => filter.id === field);

    if (filterItemIndex !== -1) {
      newFilters[filterItemIndex] = { id: field, value, metadata };
      props.onFiltersChange?.(newFilters);

      return;
    }

    props.onFiltersChange?.([...newFilters, { id: field, value, metadata }]);
  };

  const filtersRenderer = useMemo(() => {
    if (props.disableFilters) {
      return null;
    }

    return table.headerGroups.reduce(
      (headersGroupAcc: { id: IdType<T>; jsxFunc: () => React.ReactNode }[], currHeaderGroup: HeaderGroup<T>) => {
        return [
          ...headersGroupAcc,
          ...currHeaderGroup.headers.reduce(
            (headersAcc: { id: IdType<T>; jsxFunc: () => React.ReactNode }[], currHeader: HeaderGroup<T>) => {
              if (!currHeader.canFilter) {
                return headersAcc;
              }

              return [
                ...headersAcc,
                {
                  id: currHeader.id,
                  jsxFunc: () =>
                    currHeader.render('Filter', {
                      key: currHeader.id,
                      onChange: (value: FilterValue, metadata?: FilterMetadata) => promptFiltersChange(currHeader.id, value, metadata),
                      placeholder: props.columnsOptions?.[currHeader.id]?.fiterPlaceHolder || currHeader.render('Header'),
                    }),
                },
              ];
            },
            [],
          ),
        ];
      },
      [],
    );
  }, [table.headerGroups, props.disableFilters]);

  useEffect(() => {
    if (!props.onFiltersRender) {
      return;
    }

    const filters = filtersRenderer || [];
    const ids = filters.map((filter: { id: IdType<T> }) => String(filter.id)).join(',');
    const prevRenderedFilters = renderedFiltersRef.current;

    if (prevRenderedFilters?.ids === ids && prevRenderedFilters.filters === table.state.filters) {
      return;
    }

    renderedFiltersRef.current = { ids, filters: table.state.filters };
    props.onFiltersRender(filters);
  }, [filtersRenderer, props.onFiltersRender, table.state.filters]);

  useEffect(() => {
    table.setAllFilters((prev: Filters<T>) => {
      if (props.filters) {
        return props.filters;
      }

      return prev;
    });
  }, [props.filters]);

  useEffect(() => {
    if (Array.isArray(props.sort)) {
      table.setSortBy(props.sort);
    }
  }, [props.sort]);

  useEffect(() => {
    if (props.page && table.gotoPage) {
      table.gotoPage(props.page);
    }
  }, [props.page]);

  const selectedIds = useMemo(() => Object.keys(table.state.selectedRowIds).map(Number), [table.state.selectedRowIds]);

  useEffect(() => {
    props.onSelect?.(selectedIds);
  }, [selectedIds, props.onSelect]);

  const isRowSelected = (row: Row<T>): boolean => {
    const selectedRow = props.selectedRow;

    if (!Array.isArray(selectedRow)) {
      return !!selectedRow && Object.keys(selectedRow).every((key: keyof T) => selectedRow[key] === row.original[key]);
    }

    const isSelected = selectedRow.some((item: Partial<T>) => Object.keys(item).every((key: keyof T) => item[key] === row.original[key]));

    return isSelected;
  };

  useImperativeHandle(ref, () => {
    return {
      setSelectedRows(condition: (row: Row<T>, index: number) => boolean) {
        table.rows.forEach((row: Row<T>, index: number) => row.toggleRowSelected(condition(row, index)));
      },
      setFilters(updateFunc: (prevFilters: Filters<T>) => Filters<T>) {
        table.setAllFilters((prev: Filters<T>) => {
          const newFilters = updateFunc(prev);

          props.onFiltersChange?.(newFilters);

          return newFilters;
        });
      },
      setSort() {
        console.warn('set sort');
      },
      toggleAllRowsSelected: table.toggleAllRowsSelected,
    };
  }, [table, table.setAllFilters, props.onFiltersChange]);

  const updateSort = (name: string) => {
    let newSort = [...table.state.sortBy];
    const columnIndex = newSort.findIndex((sort: { id: IdType<T>; desc?: boolean | undefined }) => sort.id === name);

    if (columnIndex === -1) {
      newSort = [{ id: name, desc: false }]; //TODO:  Mooving from multy to single sort not fully refactored
    } else if (!newSort[columnIndex].desc) {
      newSort[columnIndex] = {
        ...newSort[columnIndex],
        desc: true,
      };
    } else {
      newSort.splice(columnIndex, 1);
    }

    props.onSortChange?.(newSort);
  };

  const onScrollContainer = (e: UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop + 15 >= e.currentTarget.scrollHeight - e.currentTarget.clientHeight) {
      props.onEndOfScroll?.(e.currentTarget.scrollTop);
    }
  };

  return (
    <MTableView
      table={table}
      scrollTableRef={props.scrollTableRef}
      darkMode={darkMode}
      loading={!!props.loading}
      rowClassName={props.rowClassName}
      disableFilters={props.disableFilters || !!props.onFiltersRender}
      noResultsClassName={props.noResultsClassName}
      className={props.className}
      hideSelection={props.hideSelection}
      isRowSelected={isRowSelected}
      renderExpand={props.renderExpand}
      onRowClick={props.onRowClick}
      onFilterChange={promptFiltersChange}
      onHeaderClick={updateSort}
      hiddenColumns={props.hiddenColumns || []}
      columnCenter={props.columnCenter}
      selectedTable={props.selectTable}
      hideNoResultDarkIcon={props.hideNoResultDarkIcon}
      onScrollContainer={onScrollContainer}
      onMoveRow={props.onMoveRow}
      isDraggable={props.isDraggable}
      hiddenHeader={props.hiddenHeader}
      noResultImage={props.noResultImage}
      noResultsText={props.noResultsText}
      noResultsSubHeaderText={props.noResultsSubHeaderText}
    ></MTableView>
  );
};

MTable.displayName = 'MTable';

export default React.forwardRef(MTable) as <T extends RowData = RowData>(
  props: React.PropsWithoutRef<React.PropsWithChildren<Props<T>>> & React.RefAttributes<TableRef<T>>,
) => JSX.Element;
